import { computed } from "vue";
import { usePositionViewState } from "./usePositionViewState";
import { useDropdownItems } from "@/composables/dropdown/useDropdownItems";
import { useSidebarCountStore } from "@/store/pinia/sidebarCount";
import { usePositions } from "@/composables/positions/usePositions";

export function useTableConfig() {
  const viewState = usePositionViewState();
  const dropdownItems = useDropdownItems();
  const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
  const sidebarCountStore = useSidebarCountStore();
  const { getEmployeeAvatar } = usePositions();

  const initializeDropdownFlags = async () => {
    await dropdownItems.initializeFlags();
  };

  const isNotEmpty = (val) => {
    if (Array.isArray(val)) {
      return val.length > 0;
    }
    if (val && typeof val === "object") {
      return Object.keys(val).length > 0;
    }
    return false;
  };

  function getEmployeeInfo(assigned_preparing_for) {
    const data = Array.isArray(assigned_preparing_for)
      ? assigned_preparing_for[0]
      : assigned_preparing_for;

    if (!data || typeof data !== "object") {
      return { photoUrl: "", firstName: "", lastName: "" };
    }

    const nameParts = (data.assigned_employee_name || "").split(" ");

    return {
      photoUrl: data.assigned_employee_avatar || "",
      firstName: nameParts[0] || "",
      lastName: nameParts[1] || "",
    };
  }

  const tableParams = computed(() => {
    return {
      headers: [
        {
          text: "Position",
          sortable: true,
          value: "name",
        },
        {
          text: "Primary",
          sortable: false,
          value: "primary",
          multiple: true,
          data: [
            {
              text: "Occupied by ",
              img: "employee-organization/primary-assignment-grey.svg",
            },
            // {
            //   text: "Acting As",
            //   img: "employee-organization/theater.svg",
            // },
          ],
        },
        // {
        //   text: "Backed up by",
        //   sortable: false,
        //   value: "backedup",
        //   img: "employee-organization/backedupicon.svg",
        // },
        {
          text: "Preparing",
          sortable: false,
          value: "prepares",
          img: "icons/preparing.svg",
        },
        {
          text: "Status",
          sortable: false,
          value: "status",
        },
        {
          text: "",
          sortable: false,
          value: "chatter",
        },
        {
          text: "",
          sortable: false,
          value: "actions",
        },
      ],
      columns: [
        {
          field: "",
          component: "PositionCard",
          props: (item) => {
            return {
              class: "my-4 datatable-text-content",
              "data-position-id": item?.id,
              count: item?.roles_count || 0,
              code:
                (item?.code_str
                  ? item.code_str.split(" ")[0]
                  : item?.code
                )?.toString() || "",
              name: item?.name || "",
              occupied: item?.primary_position_effective_on ? "1" : "not",
              value: item?.capacity || 1,
              clickPopup: true,
              hideCapacitySelector: true,
            };
          },
          events: (item) => ({
            input: async (capacity) => {
              await viewState.handleCapacityChange(capacity, item, accountID);
            },
            openPopup: async () => {
              await viewState.openPositionDetails(item);
            },
          }),
        },
        {
          field: "",
          component: "PrimaryPositionOccupationTag",
          props: (item) => ({
            class: "occupied_content my-4",
            ...viewState.getPrimaryPositionOccupationProps(item),
          }),
          events: (item) => ({
            onClose: () => {
              viewState.onRelievePrimaryAssignment(item);
            },
          }),
        },
        {
          field: "",
          component: "PreparePositionOccupdationTag",
          props: (item) => ({
            class: "occupied_content my-4",
            positionName: item?.name || "",
            positionCode: `#${item?.code || ""}`,
            roles: item?.roles_count || 0,
            responsibilities: item?.responsibilities_count || 0,
            nameUrl: "",
            occupiedList: [
              {
                firstName: item?.assigned_preparing_for?.assigned_employee_name
                  ? item.assigned_preparing_for.assigned_employee_name.split(
                      " "
                    )[0]
                  : "",
                lastName: item?.assigned_preparing_for?.assigned_employee_name
                  ? item.assigned_preparing_for.assigned_employee_name.split(
                      " "
                    )[1] || ""
                  : "",
                photoUrl: getEmployeeAvatar(item?.assigned_preparing_for),
                addHoverEffect: true,
              },
            ],
            occupiedByList: [],
            // preparingEmployees: item.assigned_employees
            //   .filter((emp) => emp.commitment === "Preparing for")
            //   .map((emp) => ({
            //     photoUrl: emp.avatar,
            //     firstName: emp.first_name,
            //     lastName: emp.last_name,
            //     effective_date: emp.effective_date,
            //   })),
            // preparingEmployees:
            //   item.assigned_employees.filter(
            //     (el) => el.assigned_employee_commitment == 3
            //   ).length > 0
            //     ? item.assigned_employees.slice(0, 1).map((emp) => {
            //         const newEl = {};
            //         newEl.photoUrl = emp.assigned_employee_avatar;
            //         newEl.firstName = emp.assigned_employee_name.split(" ")[0];
            //         newEl.lastName = emp.assigned_employee_name.split(" ")[1];
            //         newEl.effective_date = "";
            //         return newEl;
            //       })
            //     : [],
            preparingEmployees: isNotEmpty(item?.assigned_preparing_for)
              ? [
                  {
                    ...getEmployeeInfo(item?.assigned_preparing_for),
                    effective_date: "",
                  },
                ]
              : [],
            effectiveDate: item?.preparing_as_position_effective_on,
            employeeId: item?.assigned_employees?.[0]?.assigned_employee_id,
          }),
          events: (item) => ({
            onClose: () => viewState.onRelievePrepareAssignment(item),
          }),
        },
        {
          field: "",
          component: "StatusChip",
          props: (item) => ({
            text: item?.status || "",
            textColor: viewState.getStatusColor(item?.status),
            borderColor: viewState.getStatusColor(item?.status),
            backgroundColor: viewState.getStatusColor(item?.status),
            dotColor: viewState.getStatusColor(item?.status),
            archived: item?.archived || false,
          }),
        },
        {
          field: "",
          component: "ChatterButton",
          props: () => ({
            class: "my-4",
          }),
          events: (item) => ({
            onClick: () => {
              viewState.openPositionChatterModal(item);
            },
          }),
        },
        {
          field: "",
          component: "Dropdown",
          props: (item) => ({
            "data-testid": "position-operations-menu",
            translateXOffset: 12,
            translateYOffset: 30,
            isOpen: false,
            dropdownItems: dropdownItems.getOrgObjectDropdownItems(item || {}),
          }),
          events: (item) => ({
            onClick: async (operation) => {
              await viewState.handlePositionDropdownOperation(operation, item);
            },
          }),
        },
      ],
      filters: {
        values: viewState.state.filters.values,
        searchText: viewState.state.filters.searchText,
      },
      hideTopSection: false,
      hideActiveNo: false,
      activeRecords: {
        field: "status",
        value: "active",
      },
      defaultOrderingField: "name",
      activeRecordsCount: sidebarCountStore.positions,
      actionLabel: "Add Position",
      hideGridView: true, //------| add this to hide bulk action in ER-4046 |------
      title: "Positions Organization",
      // Optional key for UniversalTable to restore sort icon from localStorage
      orderingStorageKey: "positions.ordering",
      getRowStyle: (item) => ({
        backgroundColor: item?.status === "active" ? "#ECFDF3" : "#FFFFFF",
      }),
    };
  });

  return {
    tableParams,
    initializeDropdownFlags,
  };
}
